"use client";

import { Select as BaseSelect } from "@base-ui/react/select";
import { ChevronDownIcon } from "lucide-react";
import { type ReactNode } from "react";
import { tw } from "~/utilities/styles";
import { RequiredIndicator } from "./RequiredIndicator";

type SelectSize = "sm" | "md" | "lg";

type SelectItem = {
  value: string;
  label: string;
};

type RootProps = {
  label?: string;
  required?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  trigger?: ReactNode;
  items?: SelectItem[];
  children: ReactNode;
  size?: SelectSize;
};

function SelectRoot({
  label,
  required,
  placeholder,
  trigger,
  children,
  value,
  onChange,
  items,
  size = "md",
}: RootProps) {
  return (
    <div className="flex flex-col gap-1">
      {Boolean(label) && (
        <div className="flex flex-row items-center justify-between">
          <span className="font-medium text-[13px]">{label}</span>
          {required !== undefined && <RequiredIndicator required={required} />}
        </div>
      )}

      <BaseSelect.Root
        value={value}
        onValueChange={(v) => {
          if (v !== null) onChange?.(v);
        }}
        items={items}
      >
        {trigger ?? (
          <SelectTrigger size={size}>
            <BaseSelect.Value placeholder={placeholder} />
          </SelectTrigger>
        )}
        {children}
      </BaseSelect.Root>
    </div>
  );
}

const triggerSizeStyles = {
  sm: tw`rounded-md px-2.5 py-1.5 text-xs`,
  md: tw`rounded-md px-3 py-[7px] text-[13px]`,
  lg: tw`rounded-md px-3 py-[7px] text-[13px]`,
  icon: tw`h-8 w-8 justify-center rounded-md`,
};

function SelectTrigger({
  children,
  className,
  noChevron,
  size,
}: {
  children?: ReactNode;
  className?: string;
  noChevron?: boolean;
  size?: keyof typeof triggerSizeStyles;
}) {
  return (
    <BaseSelect.Trigger
      className={`hoverable flex cursor-default select-none items-center gap-2 whitespace-nowrap border bg-surface font-normal focus-visible:ring-2 focus-visible:ring-fg/15 ${size ? triggerSizeStyles[size] : ""} ${className ?? ""}`}
    >
      {children}
      {!noChevron && (
        <BaseSelect.Icon className="ml-auto shrink-0 opacity-90">
          <ChevronDownIcon size={16} />
        </BaseSelect.Icon>
      )}
    </BaseSelect.Trigger>
  );
}

function SelectPopup({
  children,
  align,
}: {
  children: ReactNode;
  align?: "start" | "center" | "end";
}) {
  return (
    <BaseSelect.Portal>
      <BaseSelect.Positioner
        className="z-50 outline-none"
        sideOffset={4}
        align={align}
        alignItemWithTrigger={false}
      >
        <BaseSelect.Popup className="min-w-(--anchor-width) overflow-hidden rounded-lg border bg-surface p-2 shadow-[0_12px_60px_var(--black-a3),0_12px_32px_-16px_var(--gray-a5)] dark:shadow-[0_12px_60px_var(--black-a5),0_12px_32px_-16px_var(--black-a7)]">
          <BaseSelect.ScrollUpArrow className="flex h-4 w-full cursor-default items-center justify-center" />
          <BaseSelect.List className="max-h-(--available-height) overflow-y-auto outline-none">
            {children}
          </BaseSelect.List>
          <BaseSelect.ScrollDownArrow className="flex h-4 w-full cursor-default items-center justify-center" />
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  );
}

function SelectItem({
  value,
  children,
}: {
  value: string;
  children: ReactNode;
}) {
  return (
    <BaseSelect.Item
      value={value}
      className="flex h-8 cursor-default select-none items-center rounded px-3 font-normal text-[13px] outline-none data-highlighted:not-data-selected:bg-sub data-selected:bg-fg/5"
    >
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
    </BaseSelect.Item>
  );
}

export const Select = {
  Root: SelectRoot,
  Trigger: SelectTrigger,
  Popup: SelectPopup,
  Item: SelectItem,
};
